import React, { useCallback, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, UploadCloud, FileText, AlertTriangle, CheckCircle2, ArrowRight } from "lucide-react";
import * as XLSX from "xlsx";
import { parseTRDate, parseTRAmount, normalizeSheetDates } from "@/lib/excelDate";

// Column indexes (0-based) required by user:
//   A(0)=Fatura No, C(2)=Fatura Tarihi, E(4)=Alıcı VKN/TCKN, F(5)=Alıcı, H(7)=Ödenecek Tutar
const COLS = { number: 0, date: 2, tax: 4, name: 5, amount: 7 };

const stripLeadingApos = (v) => String(v ?? "").replace(/^'+/, "").trim();

export default function InvoiceImportModal({ onClose, onImported, direction = "sales" }) {
  const isSales = direction === "sales";
  const partyLabel = isSales ? "Alıcı" : "Gönderici";
  const contactLabelTr = isSales ? "Alıcı" : "Gönderici";
  const kindLabel = isSales ? "Satış (Borç)" : "Alış (Alacak)";
  const [step, setStep] = useState("upload"); // upload | preview | result
  const [rows, setRows] = useState([]); // [{number, date, tax, name, amount, _error}]
  const [autoCreate, setAutoCreate] = useState(true);
  const [defaultTerm, setDefaultTerm] = useState(""); // days, applied to newly-created contacts
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFile = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target.result);
        // Read as workbook (xlsx handles CSV too, incl. BOM & UTF-8)
        // raw:true prevents XLSX from auto-interpreting "06.08.2026" TR-format
        // CSV date strings as US MM/DD dates. Excel-native numeric date cells
        // are still converted to TR strings via normalizeSheetDates below.
        const wb = XLSX.read(data, { type: "array", codepage: 65001, raw: true, cellNF: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        normalizeSheetDates(ws);
        // sheet_to_json with header:1 gives 2D array; FS param supports both , and ; via detection
        const arr = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: "", raw: false, dateNF: "dd.mm.yyyy" });
        if (!arr.length) {
          toast.error("Dosya boş");
          return;
        }
        // Detect header row - skip if row[0] contains "fatura no" (case-insensitive TR)
        let startIdx = 0;
        const first = arr[0].map((x) => String(x || "").toLowerCase());
        if (first.some((c) => c.includes("fatura no") || c.includes("fatura tarihi") || c.includes("vkn"))) {
          startIdx = 1;
        }
        const parsed = [];
        for (let i = startIdx; i < arr.length; i++) {
          const r = arr[i];
          const num = stripLeadingApos(r[COLS.number]);
          if (!num) continue; // skip empty rows
          const dateRaw = r[COLS.date];
          const tax = stripLeadingApos(r[COLS.tax]);
          const name = String(r[COLS.name] || "").trim();
          const amountRaw = r[COLS.amount];
          const iso = parseTRDate(dateRaw);
          const amt = parseTRAmount(amountRaw);
          let err = "";
          if (!iso) err = "Tarih geçersiz";
          else if (isNaN(amt) || amt <= 0) err = "Tutar geçersiz";
          else if (!name && !tax) err = "Cari bilgisi yok";
          parsed.push({
            invoice_number: num,
            issue_date: iso,
            issue_date_raw: String(dateRaw || ""),
            tax_id: tax,
            contact_name: name,
            amount: isNaN(amt) ? null : amt,
            amount_raw: String(amountRaw || ""),
            _error: err,
          });
        }
        if (!parsed.length) {
          toast.error("Aktarılabilecek satır bulunamadı");
          return;
        }
        setRows(parsed);
        setStep("preview");
      } catch (e) {
        console.error(e);
        toast.error("Dosya okunamadı: " + (e?.message || ""));
      }
    };
    reader.onerror = () => toast.error("Dosya okunamadı");
    reader.readAsArrayBuffer(file);
  }, []);

  const stats = useMemo(() => {
    const total = rows.length;
    const invalid = rows.filter((r) => r._error).length;
    const valid = total - invalid;
    const totalAmount = rows.reduce((s, r) => s + (r._error ? 0 : Number(r.amount) || 0), 0);
    return { total, valid, invalid, totalAmount };
  }, [rows]);

  const submit = async () => {
    const items = rows.filter((r) => !r._error).map((r) => ({
      invoice_number: r.invoice_number,
      issue_date: r.issue_date,
      tax_id: r.tax_id,
      contact_name: r.contact_name,
      amount: r.amount,
    }));
    if (!items.length) {
      toast.error("Aktarılacak geçerli satır yok");
      return;
    }
    setUploading(true);
    try {
      const { data } = await api.post("/invoices/bulk-import", {
        items,
        auto_create_contacts: autoCreate,
        direction,
        default_payment_term_days:
          defaultTerm === "" || defaultTerm === null ? null : Number(defaultTerm),
      });
      setResult(data);
      setStep("result");
      toast.success(`${data.imported} fatura aktarıldı`);
      onImported?.();
    } catch (e) {
      toast.error("Aktarım hatası: " + (e?.response?.data?.detail || e?.message || ""));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="invoice-import-modal">
      <div className="brutal-card bg-white w-full max-w-4xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between border-b-2 border-black px-5 py-3">
          <div className="flex items-center gap-2">
            <FileText size={18} />
            <h2 className="font-display text-xl font-bold">
              CSV/Excel'den {isSales ? "Satış" : "Alış"} Faturası Aktar
            </h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-black/5" data-testid="invoice-import-close">
            <X size={18} />
          </button>
        </div>

        {step === "upload" && (
          <div className="p-6 space-y-4">
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files?.[0];
                if (f) handleFile(f);
              }}
              className="border-2 border-dashed border-black/30 p-10 text-center cursor-pointer hover:bg-neutral-50"
              data-testid="invoice-import-dropzone"
            >
              <UploadCloud size={44} className="mx-auto text-neutral-500" />
              <p className="mt-3 font-semibold">CSV veya Excel dosyası seçin ya da sürükleyin</p>
              <p className="text-xs text-neutral-500 mt-1">.csv / .xlsx / .xls</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="invoice-import-file"
            />
            <div className="bg-neutral-50 border border-black/10 p-4 text-xs">
              <p className="font-semibold uppercase tracking-widest mb-2">Beklenen sütun düzeni</p>
              <ul className="space-y-1 text-neutral-700">
                <li><b>A</b> — Fatura No</li>
                <li><b>C</b> — Fatura Tarihi (17.07.2026 formatında)</li>
                <li><b>E</b> — {partyLabel} VKN/TCKN</li>
                <li><b>F</b> — {partyLabel} (cari adı)</li>
                <li><b>H</b> — Ödenecek Tutar</li>
              </ul>
              <p className="text-neutral-500 mt-2">Diğer sütunlar dikkate alınmaz. Başlık satırı otomatik atlanır. Tüm faturalar <b>{kindLabel}</b> olarak eklenir. Aynı numaralı fatura varsa atlanır.</p>
            </div>
          </div>
        )}

        {step === "preview" && (
          <>
            <div className="p-4 border-b border-black/10 flex flex-wrap items-center gap-4">
              <div className="text-sm">
                <span className="label-caps mr-2">Okunan:</span>
                <span className="font-mono-num font-semibold">{stats.total}</span>
                <span className="mx-3 text-neutral-300">|</span>
                <span className="label-caps mr-2">Geçerli:</span>
                <span className="font-mono-num font-semibold text-emerald-700">{stats.valid}</span>
                {stats.invalid > 0 && (
                  <>
                    <span className="mx-3 text-neutral-300">|</span>
                    <span className="label-caps mr-2">Hatalı:</span>
                    <span className="font-mono-num font-semibold text-rose-600">{stats.invalid}</span>
                  </>
                )}
                <span className="mx-3 text-neutral-300">|</span>
                <span className="label-caps mr-2">Toplam:</span>
                <span className="font-mono-num font-semibold">
                  {stats.totalAmount.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                </span>
              </div>
              <label className="ml-auto flex items-center gap-2 text-xs cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={autoCreate}
                  onChange={(e) => setAutoCreate(e.target.checked)}
                  data-testid="invoice-import-autocreate"
                />
                Eksik carileri otomatik oluştur
              </label>
              <label className="flex items-center gap-2 text-xs">
                <span>Yeni cariler için vade (gün):</span>
                <input
                  type="number"
                  min="0"
                  max="365"
                  placeholder="Boş = peşin"
                  value={defaultTerm}
                  onChange={(e) => setDefaultTerm(e.target.value)}
                  disabled={!autoCreate}
                  data-testid="invoice-import-default-term"
                  className="border border-black/20 px-2 py-1 w-24 font-mono-num disabled:opacity-40"
                />
              </label>
            </div>
            <div className="flex-1 overflow-auto">
              <table className="w-full text-xs" data-testid="invoice-import-preview-table">
                <thead className="bg-neutral-50 sticky top-0 z-10">
                  <tr className="border-b-2 border-black/80">
                    <th className="text-left px-3 py-2 label-caps">Fatura No</th>
                    <th className="text-left px-3 py-2 label-caps">Tarih</th>
                    <th className="text-left px-3 py-2 label-caps">VKN/TCKN</th>
                    <th className="text-left px-3 py-2 label-caps">{contactLabelTr}</th>
                    <th className="text-right px-3 py-2 label-caps">Tutar</th>
                    <th className="text-left px-3 py-2 label-caps">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className={`border-b border-black/5 ${r._error ? "bg-rose-50" : ""}`}>
                      <td className="px-3 py-1.5 font-mono-num">{r.invoice_number}</td>
                      <td className="px-3 py-1.5 font-mono-num">{r.issue_date || <span className="text-rose-600">{r.issue_date_raw}</span>}</td>
                      <td className="px-3 py-1.5 font-mono-num text-neutral-600">{r.tax_id || "—"}</td>
                      <td className="px-3 py-1.5">{r.contact_name || "—"}</td>
                      <td className="px-3 py-1.5 text-right font-mono-num">
                        {r.amount != null
                          ? Number(r.amount).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : <span className="text-rose-600">{r.amount_raw}</span>}
                      </td>
                      <td className="px-3 py-1.5">
                        {r._error
                          ? <span className="text-rose-700 text-[10px] uppercase tracking-widest flex items-center gap-1"><AlertTriangle size={10} /> {r._error}</span>
                          : <span className="text-emerald-700 text-[10px] uppercase tracking-widest">Hazır</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-black/10 p-4 flex items-center justify-between gap-2">
              <button
                onClick={() => { setRows([]); setStep("upload"); }}
                className="brutal-btn bg-white text-black border border-black px-4 py-2 text-sm"
                data-testid="invoice-import-back"
              >
                Geri
              </button>
              <button
                onClick={submit}
                disabled={uploading || stats.valid === 0}
                className="brutal-btn bg-black text-white px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-40"
                data-testid="invoice-import-submit"
              >
                {uploading ? "Aktarılıyor…" : `${stats.valid} Faturayı Aktar`}
                <ArrowRight size={14} />
              </button>
            </div>
          </>
        )}

        {step === "result" && result && (
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={40} className="text-emerald-600" />
              <div>
                <h3 className="font-display text-2xl font-bold">Aktarım Tamamlandı</h3>
                <p className="text-sm text-neutral-600">Sonuç aşağıda özetlenmiştir.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <ResultStat label="Aktarılan" value={result.imported} tone="emerald" />
              <ResultStat label="Atlanan (Mükerrer)" value={result.skipped_duplicates} />
              <ResultStat label="Oluşan Cari" value={result.created_contacts} tone="emerald" />
              <ResultStat label="Hatalı" value={result.error_count} tone={result.error_count > 0 ? "rose" : ""} />
            </div>
            {result.errors && result.errors.length > 0 && (
              <div className="max-h-56 overflow-auto border border-rose-200 bg-rose-50 p-3">
                <p className="font-semibold text-xs uppercase tracking-widest text-rose-700 mb-2">Hatalar</p>
                <ul className="text-xs space-y-1 text-rose-800">
                  {result.errors.map((e, i) => (
                    <li key={i}>Satır {e.row}: {e.message}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="brutal-btn bg-black text-white px-4 py-2 text-sm"
                data-testid="invoice-import-done"
              >
                Kapat
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ResultStat({ label, value, tone }) {
  const toneClass = tone === "emerald" ? "text-emerald-700" : tone === "rose" ? "text-rose-600" : "text-black";
  return (
    <div className="border-2 border-black p-3">
      <p className="label-caps text-[10px]">{label}</p>
      <p className={`font-mono-num text-2xl font-bold mt-1 ${toneClass}`}>{value ?? 0}</p>
    </div>
  );
}
